import { Route } from "react-router";

import { useAuth } from "@/providers";
import { queryClient } from "@/providers/QueryClient";

import { Action } from "./Action";
import { ActionHub } from "./ActionHub";
import { ActionsTabs, type ActionsTabHandle } from "./ActionsTabs";
import { Invocations, OrgActions } from "./components";
import { Images } from "./components/Images";
import { CreateAction } from "./CreateAction";
import { InvokeAction } from "./InvokeAction";

/**
 * Routes for the Actions cluster. These are spread directly into the app's
 * route tree (not nested under a single `/actions` parent) because the tab
 * routes span two top-level prefixes: the Invocations list lives at
 * `/invocations` so it shares a prefix with its detail pages, while
 * Actions/Hub/Images stay under `/actions`.
 *
 * The list routes share the `ActionsTabs` layout (page chrome + tab bar) via a
 * pathless layout route and render their panel through its <Outlet />. Detail
 * and create pages sit outside the layout, so they render without the tab bar.
 * The invocation detail route itself is registered in Router.tsx.
 */
export const ActionsRoutes = () => [
  <Route key={"tabs"} element={<ActionsTabs />}>
    <Route
      path="/actions"
      element={<ActionsPanel />}
      handle={{ actionsTab: "actions" } satisfies ActionsTabHandle}
    />
    <Route
      path="/actions/hub"
      element={<ActionHub />}
      handle={{ actionsTab: "hub" } satisfies ActionsTabHandle}
    />
    <Route
      path="/actions/images"
      element={<ImagesPanel />}
      handle={{ actionsTab: "images" } satisfies ActionsTabHandle}
    />
    <Route
      path="/invocations"
      element={<InvocationsPanel />}
      handle={{ actionsTab: "invocations" } satisfies ActionsTabHandle}
    />
  </Route>,
  <Route key={"create"} path="/actions/create" element={<CreateAction />} />,
  <Route
    key={"invoke"}
    path="/actions/invoke"
    element={<InvokeAction.Component />}
    loader={InvokeAction.loader(queryClient)}
    shouldRevalidate={InvokeAction.shouldRevalidate}
  />,
  <Route key="action" path="/actions/:name" element={<Action />} />,
  <Route key="ownedAction" path="/actions/:owner/:name" element={<Action />} />,
];

function ActionsPanel() {
  const { currentOrganization } = useAuth();
  return <OrgActions currentOrg={currentOrganization} />;
}

function InvocationsPanel() {
  const { currentOrganization } = useAuth();
  return <Invocations currentOrg={currentOrganization} />;
}

function ImagesPanel() {
  const { currentOrganization } = useAuth();
  return <Images currentOrg={currentOrganization} />;
}
